import { memo, ReactNode, ComponentType } from 'react';
import clsx from 'clsx';
import { ComponentMemo } from '@headless-workspace/typings';
import {
    GlowClickable,
    GlowClickableWithRightIcon,
    GlowIcon,
    GlowTextBody,
    GlowTextBodySmall,
    Icons,
    PropsWithAriaLabel,
    PropsWithClassName,
    PropsWithCTA,
} from '../../../../src';

type GlowMenuButton = PropsWithAriaLabel &
    PropsWithClassName &
    PropsWithCTA & {
        iconClassName?: string;
        label: string | ReactNode;
        labelColor?: string;
        Icon?: ComponentType<{
            fill?: string;
        }>;
        iconFill?: string;
        showArrow?: boolean;
        isCollapsible?: boolean;
        isCollapsed?: boolean;
        fontSize?: 'body' | 'bodySmall';
    };

/**
 * Component called MenuCategories and MenuSubCategories/Head on FIGMA
 */
export const GlowMenuButton: ComponentMemo<GlowMenuButton> = memo(
    ({
        className,
        iconClassName,
        ariaLabel,
        label,
        labelColor,
        Icon,
        iconFill,
        showArrow = false,
        isCollapsible = false,
        isCollapsed = false,
        fontSize = 'body',
        ...props
    }) => {
        'use memo';

        const LabelComponent = fontSize === 'bodySmall' ? GlowTextBodySmall : GlowTextBody;
        const labelStyle = showArrow ? { color: labelColor } : labelColor ? { color: 'inherit' } : undefined;
        const labelFontWeight = isCollapsed ? 'bold' : 'medium';

        const renderLabelElements = () => (
            <LabelComponent
                text={label}
                fontWeight={labelFontWeight}
                className="text-wrap text-left"
                style={labelStyle}
                TagName="span"
            />
        );

        const renderIconElement = () =>
            Icon && <GlowIcon className={iconClassName} Icon={Icon} type={'large'} fill={iconFill} />;

        if (!showArrow) {
            return (
                <GlowClickable
                    className={clsx('p-1 desktop:px-0 desktop:py-0.5 gap-0.5', className)}
                    style={{ color: labelColor }}
                    ariaLabel={ariaLabel}
                    content={{
                        labelElement: renderLabelElements(),
                        icon: renderIconElement(),
                    }}
                    {...props}
                />
            );
        }

        return (
            <GlowClickableWithRightIcon
                className={clsx('p-1 desktop:px-0 desktop:py-0.5 gap-0.5', className)}
                ariaLabel={ariaLabel}
                content={{
                    labelElement: renderLabelElements(),
                    icon: renderIconElement(),
                }}
                rightIcon={isCollapsible ? Icons.ChevronDown : Icons.ChevronRight}
                iconClassName={clsx(
                    isCollapsible && 'duration-moderate1 ease-linear fill-text-primary',
                    isCollapsed && 'rotate-180',
                )}
                {...props}
            />
        );
    },
);

GlowMenuButton.displayName = 'GlowMenuButton';
